import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faSpinner, faCheckCircle, faExclamationTriangle,
    faSave, faCamera
} from '@fortawesome/free-solid-svg-icons';
import MemberPhotoAvatar from './MemberPhotoAvatar';

import { memberService, familyService } from '../services';
import { validateMemberForm } from '../services/validation';

import './FormPage.css';

// Helper functions for birthday dropdowns
const getMonths = () => Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(0, i).toLocaleString('default', { month: 'long' }) }));
const getDaysInMonth = (month, year) => {
    if (!month) return Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
    const yearToUse = (year && parseInt(year, 10)) ? parseInt(year, 10) : 2000;
    const days = new Date(yearToUse, parseInt(month, 10), 0).getDate();
    return Array.from({ length: days }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
};
const getYears = () => {
    const years = [{ value: '', label: 'Year (Optional)' }];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 120; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};

function EditMemberPage() {
    const { memberId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // State for initial data to compare against
    const [initialMemberData, setInitialMemberData] = useState(null);

    // Form fields state
    const [memberName, setMemberName] = useState('');
    const [selectedBirthMonth, setSelectedBirthMonth] = useState('');
    const [selectedBirthDay, setSelectedBirthDay] = useState('');
    const [selectedBirthYear, setSelectedBirthYear] = useState('');
    const [cellphone, setCellphone] = useState('');
    const [email, setEmail] = useState('');
    const [familyId, setFamilyId] = useState('');
    const [familyName, setFamilyName] = useState('');
    const [currentPhotoPath, setCurrentPhotoPath] = useState(null);

    // Component status state
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isReady, setIsReady] = useState(false);

    // Check auth status
    useEffect(() => {
        const checkAuth = async () => {
            try {
                await memberService.getOrgId();
                setIsReady(true);
            } catch (err) {
                setError(err.message || "Could not determine organization ID. Please log in again.");
                setLoading(false);
                setIsReady(false);
            }
        };
        checkAuth();
    }, []);

    // Check for returned photo path from navigation state
    useEffect(() => {
        if (location.state?.newPhotoPath) {
            setCurrentPhotoPath(location.state.newPhotoPath === 'REMOVED' ? null : location.state.newPhotoPath);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    // Fetch Member Data
    const fetchMemberData = useCallback(async () => {
        if (!isReady || !memberId) return;

        setLoading(true);
        setError(null);

        try {
            const member = await memberService.getMember(memberId);

            if (!member) {
                throw new Error("Member not found or you do not have permission to edit it.");
            }

            setInitialMemberData(member);
            setMemberName(member.name || '');
            setCellphone(member.cellphone || '');
            setEmail(member.email || '');
            setFamilyId(member.familyId || '');
            setCurrentPhotoPath(member.photo || null);
            setSelectedBirthMonth(member.birthdayMonth || '');
            setSelectedBirthDay(member.birthdayDay || '');
            setSelectedBirthYear(member.birthdayYear || '');

            if (member.familyId) {
                const family = await familyService.getFamily(member.familyId);
                setFamilyName(family ? (family.surname || 'Unnamed Family') : 'Unknown Family');
            } else {
                setFamilyName('No Associated Family');
            }
        } catch (err) {
            setError(err.message || "Failed to load member details.");
        } finally {
            setLoading(false);
        }
    }, [memberId, isReady]);

    useEffect(() => {
        if (isReady) {
            fetchMemberData();
        }
    }, [isReady, fetchMemberData]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setSuccessMessage(null);

        const formData = {
            name: memberName,
            email: email,
            cellphone: cellphone,
            birthdayMonth: selectedBirthMonth,
            birthdayDay: selectedBirthDay,
            birthdayYear: selectedBirthYear,
        };

        const validation = validateMemberForm(formData);
        if (!validation.isValid) {
            const firstError = Object.values(validation.errors)[0];
            setError(firstError);
            return;
        }

        setSubmitting(true);

        try {
            const updateData = { memberId };
            if (memberName.trim() !== initialMemberData.name) {
                updateData.name = memberName.trim();
            }
            if ((email || null) !== (initialMemberData.email || null)) {
                updateData.email = email.trim() || null;
            }
            if ((cellphone || null) !== (initialMemberData.cellphone || null)) {
                updateData.cellphone = cellphone.trim() || null;
            }
            if (currentPhotoPath !== (initialMemberData.photo || null)) {
                updateData.photo = currentPhotoPath;
            }

            const bMonth = selectedBirthMonth || null;
            const bDay = selectedBirthDay || null;
            const bYear = selectedBirthYear || null;

            if (bMonth !== (initialMemberData.birthdayMonth || null) ||
                bDay !== (initialMemberData.birthdayDay || null) ||
                bYear !== (initialMemberData.birthdayYear || null)) {
                updateData.birthdayMonth = bMonth;
                updateData.birthdayDay = bDay;
                updateData.birthdayYear = bYear;
            }

            if (Object.keys(updateData).length <= 1) {
                setSuccessMessage("No changes were made.");
                setSubmitting(false);
                return;
            }

            const result = await memberService.updateMember(updateData);

            if (result.success) {
                setSuccessMessage(result.message || "Member updated successfully!");
                fetchMemberData();
                setTimeout(() => {
                    const returnTo = familyId ? `/families/${familyId}` : '/families';
                    navigate(returnTo, { state: { message: result.message } });
                }, 1500);
            } else {
                throw new Error(result.error || "The server failed to update the member.");
            }
        } catch (err) {
            setError(err.message || "An unexpected error occurred during the update.");
        } finally {
            setSubmitting(false);
        }
    };

    const availableMonths = useMemo(() => getMonths(), []);
    const availableDays = useMemo(() => getDaysInMonth(selectedBirthMonth, selectedBirthYear), [selectedBirthMonth, selectedBirthYear]);
    const availableYears = useMemo(() => getYears(), []);

    // Loading state
    if (loading) {
        return (
            <div className="form-page">
                <div className="form-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-form"></div>
                </div>
            </div>
        );
    }

    // Error state - can't load member
    if (error && !initialMemberData) {
        return (
            <div className="form-page">
                <div className="form-page-header">
                    <div className="container">
                        <div className="form-page-header-content">
                            <div className="form-page-header-left">
                                <Link to="/families" className="form-back-btn" title="Back to Families">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="form-page-title">Edit Member</h1>
                                    <p className="form-page-subtitle">Error loading member</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="form-card">
                        <div className="form-card-body">
                            <div className="form-alert form-alert-danger">
                                <div className="form-alert-icon">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                </div>
                                <div className="form-alert-content">{error}</div>
                            </div>
                            <Link to="/families" className="form-submit-btn" style={{ textDecoration: 'none' }}>
                                Back to Families
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const backLink = familyId ? `/families/${familyId}` : '/families';

    return (
        <div className="form-page">
            {/* Header */}
            <div className="form-page-header">
                <div className="container">
                    <div className="form-page-header-content">
                        <div className="form-page-header-left">
                            <Link to={backLink} className="form-back-btn" title="Go Back">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="form-page-title">Edit Member</h1>
                                <p className="form-page-subtitle">
                                    {initialMemberData?.name ? `Editing ${initialMemberData.name}` : 'Update member details'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="form-card">
                    <div className="form-card-body">
                        <form onSubmit={handleSubmit}>
                            {/* Alert Messages */}
                            {error && (
                                <div className="form-alert form-alert-danger">
                                    <div className="form-alert-icon">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                    </div>
                                    <div className="form-alert-content">{error}</div>
                                </div>
                            )}
                            {successMessage && (
                                <div className="form-alert form-alert-success">
                                    <div className="form-alert-icon">
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                    </div>
                                    <div className="form-alert-content">{successMessage}</div>
                                </div>
                            )}

                            {/* Photo Section */}
                            <div className="form-section">
                                <div className="form-photo-section">
                                    <div className="form-photo-avatar">
                                        <MemberPhotoAvatar src={currentPhotoPath} alt={memberName} size={120} />
                                    </div>
                                    <div className="form-photo-buttons">
                                        <Link
                                            to={`/families/${familyId}/assign-photos`}
                                            state={{
                                                forMemberId: memberId,
                                                forMemberName: memberName,
                                                currentPhotoPath: currentPhotoPath,
                                                returnPath: location.pathname
                                            }}
                                            className={`form-photo-btn form-photo-btn-primary ${(!familyId || submitting) ? 'disabled' : ''}`}
                                            style={{ textDecoration: 'none', pointerEvents: (!familyId || submitting) ? 'none' : 'auto', opacity: (!familyId || submitting) ? 0.5 : 1 }}
                                        >
                                            <FontAwesomeIcon icon={faCamera} />
                                            Change Photo
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            {/* Family Info (Read-only) */}
                            <div className="form-section">
                                <div className="form-section-title">Family</div>
                                <div className="form-field">
                                    <label className="form-field-label">Family</label>
                                    <input
                                        type="text"
                                        className="form-field-input"
                                        value={familyName || 'Loading...'}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>

                            {/* Basic Info Section */}
                            <div className="form-section">
                                <div className="form-section-title">Basic Information</div>

                                <div className="form-field">
                                    <label className="form-field-label">
                                        First Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-field-input"
                                        value={memberName}
                                        onChange={(e) => setMemberName(e.target.value)}
                                        placeholder="Enter first name"
                                        disabled={submitting}
                                        required
                                    />
                                </div>

                                <div className="form-field">
                                    <label className="form-field-label">Email Address</label>
                                    <input
                                        type="email"
                                        className="form-field-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="(Optional)"
                                        disabled={submitting}
                                    />
                                </div>

                                <div className="form-field">
                                    <label className="form-field-label">Cellphone</label>
                                    <input
                                        type="tel"
                                        className="form-field-input"
                                        value={cellphone}
                                        onChange={(e) => setCellphone(e.target.value)}
                                        placeholder="(Optional)"
                                        disabled={submitting}
                                    />
                                </div>
                            </div>

                            {/* Birthday Section */}
                            <div className="form-section">
                                <div className="form-section-title">Birthday (Optional)</div>

                                <div className="form-field-row form-field-row-date">
                                    <div className="form-field">
                                        <label className="form-field-label">Month</label>
                                        <select
                                            className="form-field-select"
                                            value={selectedBirthMonth}
                                            onChange={(e) => setSelectedBirthMonth(e.target.value)}
                                            disabled={submitting}
                                        >
                                            <option value="">Select Month</option>
                                            {availableMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field-label">Day</label>
                                        <select
                                            className="form-field-select"
                                            value={selectedBirthDay}
                                            onChange={(e) => setSelectedBirthDay(e.target.value)}
                                            disabled={submitting}
                                        >
                                            <option value="">Day</option>
                                            {availableDays.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field-label">Year</label>
                                        <select
                                            className="form-field-select"
                                            value={selectedBirthYear}
                                            onChange={(e) => setSelectedBirthYear(e.target.value)}
                                            disabled={submitting}
                                        >
                                            {availableYears.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="form-submit-btn"
                                    disabled={submitting || loading}
                                >
                                    {submitting ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditMemberPage;
